<template>
  <b-modal :active="active" @close="$emit('close')" has-modal-card trap-focus aria-role="dialog" aria-modal>
    <div class="modal-card" style="max-width: 420px; margin: auto;">
      <header class="modal-card-head">
        <p class="modal-card-title has-text-centered has-text-weight-bold">Add/Update Rule</p>
      </header>
      <section class="modal-card-body">
        <!-- Level info banner -->
        <div class="level-banner mb-4">
          <div class="is-flex is-justify-content-space-between is-align-items-center">
            <span class="has-text-danger is-size-7">This rule will be applied on {{ levelLabel }} level</span>
            <b-icon icon="information" type="is-danger" size="is-small"></b-icon>
          </div>
          <div class="mt-1">
            <span class="is-size-7">{{ entityLabel }}:</span>
            <span class="is-size-7 has-text-weight-bold"> {{ entityName }}</span>
          </div>
        </div>

        <!-- Rule Type -->
        <b-field label="Rule Type">
          <b-select v-model="form.archiveBy" expanded>
            <option value="timeSinceLastCaptureDays">Time Since Last Capture Days</option>
            <option value="everyXDays">Every X Days</option>
          </b-select>
        </b-field>

        <!-- timeSinceLastCaptureDays fields -->
        <div v-if="form.archiveBy === 'timeSinceLastCaptureDays'">
          <b-field label="Period from last capture">
            <b-numberinput v-model="form.timeSinceLastCaptureDays" min="1" controls-position="compact"></b-numberinput>
          </b-field>
        </div>

        <!-- everyXDays fields -->
        <div v-if="form.archiveBy === 'everyXDays'">
          <b-field label="First Day To Update">
            <b-datepicker v-model="form.everyXDaysFirstDayToUpdate" icon="calendar-today" editable
                          :date-formatter="formatDate" :date-parser="parseDate" placeholder="YYYY-MM-DD">
            </b-datepicker>
          </b-field>

          <b-field label="Days">
            <b-numberinput v-model="form.everyXDays" min="1" controls-position="compact"></b-numberinput>
          </b-field>

          <!-- Time Window -->
          <div class="time-window-section mt-4">
            <div class="is-size-7 has-text-grey mb-2" style="text-transform: uppercase;">Time Window (optional)</div>
            <b-field label="Start Time">
              <b-datetimepicker v-model="form.startTime" icon="calendar-clock" editable size="is-small"
                                :datepicker="{ showWeekNumber: false, size: 'is-small' }"
                                :timepicker="{ hourFormat: '24', size: 'is-small' }"
                                placeholder="Select start time">
              </b-datetimepicker>
            </b-field>
            <b-field label="End Time">
              <b-datetimepicker v-model="form.endTime" icon="calendar-clock" editable size="is-small"
                                :datepicker="{ showWeekNumber: false, size: 'is-small' }"
                                :timepicker="{ hourFormat: '24', size: 'is-small' }"
                                placeholder="Select end time">
              </b-datetimepicker>
            </b-field>
          </div>
        </div>
      </section>
      <footer class="modal-card-foot is-flex is-justify-content-center" style="gap: 8px;">
        <b-button v-if="isEdit" type="is-danger" expanded icon-left="delete" @click="onDelete">
          Delete Rule
        </b-button>
        <b-button type="is-success" expanded icon-left="plus" @click="onSubmit">
          {{ isEdit ? 'Update Rule' : 'Add Rule' }}
        </b-button>
      </footer>
    </div>
  </b-modal>
</template>

<script>
export default {
  name: "archive-rule-dialog",
  props: {
    active: {
      type: Boolean,
      default: false
    },
    level: {
      type: String,
      default: 'project',
      validator: (val) => ['project', 'site', 'dataDevice'].includes(val)
    },
    entityName: {
      type: String,
      default: ''
    },
    entityId: {
      type: String,
      default: ''
    },
    rule: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      form: {
        archiveBy: 'timeSinceLastCaptureDays',
        timeSinceLastCaptureDays: 180,
        everyXDays: 30,
        everyXDaysFirstDayToUpdate: null,
        startTime: null,
        endTime: null,
      }
    }
  },
  computed: {
    isEdit() {
      return this.rule !== null && Object.keys(this.rule).length !== 0
    },
    levelLabel() {
      if (this.level === 'dataDevice') return 'device'
      return this.level
    },
    entityLabel() {
      const labels = {
        project: 'Project name',
        site: 'Site name',
        dataDevice: 'Device name'
      }
      return labels[this.level] || 'Name'
    }
  },
  watch: {
    active(val) {
      if (val) {
        this.initForm()
      }
    }
  },
  methods: {
    initForm() {
      if (this.rule && Object.keys(this.rule).length > 0) {
        this.form.archiveBy = this.rule.archiveBy || 'timeSinceLastCaptureDays'
        this.form.timeSinceLastCaptureDays = this.rule.timeSinceLastCaptureDays || 180
        this.form.everyXDays = this.rule.everyXDays || 30
        this.form.everyXDaysFirstDayToUpdate = this.rule.everyXDaysFirstDayToUpdate
            ? new Date(this.rule.everyXDaysFirstDayToUpdate) : null
        if (this.rule.validProcessingPeriods && this.rule.validProcessingPeriods.length > 0) {
          const period = this.rule.validProcessingPeriods[0]
          this.form.startTime = period.dateRange && period.dateRange[0] ? new Date(period.dateRange[0]) : null
          this.form.endTime = period.dateRange && period.dateRange[1] ? new Date(period.dateRange[1]) : null
        } else {
          this.form.startTime = null
          this.form.endTime = null
        }
      } else {
        this.form.archiveBy = 'timeSinceLastCaptureDays'
        this.form.timeSinceLastCaptureDays = 180
        this.form.everyXDays = 30
        this.form.everyXDaysFirstDayToUpdate = null
        this.form.startTime = null
        this.form.endTime = null
      }
    },
    formatDate(date) {
      if (!date) return ''
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    },
    parseDate(str) {
      if (!str) return null
      const parts = str.split('-')
      return new Date(parts[0], parts[1] - 1, parts[2])
    },
    buildPayload() {
      const idFieldMap = {
        project: 'projectId',
        site: 'siteId',
        dataDevice: 'dataDeviceId'
      }

      const payload = {
        archiveBy: this.form.archiveBy,
        currentStatus: 'active',
        validProcessingPeriods: []
      }

      if (this.isEdit) {
        payload.selectedType = this.level
        payload.selectedId = this.entityId
      } else {
        payload[idFieldMap[this.level]] = this.entityId
        payload.ruleAppliedAtLevel = this.level
      }

      if (this.form.archiveBy === 'timeSinceLastCaptureDays') {
        payload.timeSinceLastCaptureDays = this.form.timeSinceLastCaptureDays
      } else {
        payload.everyXDays = this.form.everyXDays
        payload.everyXDaysFirstDayToUpdate = this.form.everyXDaysFirstDayToUpdate
            ? this.formatDate(this.form.everyXDaysFirstDayToUpdate) : null
        if (this.form.startTime && this.form.endTime) {
          payload.validProcessingPeriods = [{
            dateRange: [
              this.form.startTime.toISOString().replace('.000Z', ''),
              this.form.endTime.toISOString().replace('.000Z', '')
            ]
          }]
        }
      }

      return payload
    },
    onSubmit() {
      const payload = this.buildPayload()
      console.log('Rule payload:', payload)
      this.$emit('onSubmitRule', payload, this.isEdit)
    },
    onDelete() {
      this.$buefy.dialog.confirm({
        title: 'Delete Rule',
        message: `Are you sure you want to delete this rule for <b>${this.entityName}</b>?`,
        confirmText: 'Delete',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => {
          this.$emit('onDeleteRule', this.rule.id)
        }
      })
    }
  }
}
</script>

<style scoped lang="scss">
.level-banner {
  background-color: #fff5f5;
  border: 1px solid #ffcccc;
  border-radius: 6px;
  padding: 10px 12px;
}

.time-window-section {
  border-top: 1px solid #eee;
  padding-top: 12px;
}

.modal-card-foot {
  padding: 16px 20px;
}
</style>
